<template>
    <v-container fill-height class="align-start">
        <v-col class="align-self-center">
            <loading-overlay :loading="loading"></loading-overlay>
            <v-row class="no-gutters align-center justify-center">
                <v-col class="form">
                    <template>
                        <v-expansion-panels focusable v-model="panel">
                            <v-card
                                class="v-sheet--expansion-panels--header"
                                style="order: 1"
                            >
                                <v-toolbar color="primary" dark flat>
                                    <v-toolbar-title>Profile Settings</v-toolbar-title>
                                </v-toolbar>

                                <v-tooltip
                                    v-if="identityVerificationData && !identityVerificationData.isVerified"
                                    bottom
                                >
                                    <template
                                        v-slot:activator="{ on, attrs }"
                                    >
                                        <v-icon
                                            v-bind="attrs"
                                            class="verify"
                                            medium
                                            v-on="on"
                                            @click="beginIdentityVerification"
                                        >
                                            mdi-account-clock
                                        </v-icon>
                                    </template>
                                    <span>Verify Identity</span>
                                </v-tooltip>

                                <v-row class="mx-3">
                                    <v-col class="pb-0">
                                        <v-row no-gutters>
                                            <v-col>
                                                <h3
                                                    class="mt-6 text-center primary--text body-1 font-weight-bold"
                                                >
                                                    Avatar
                                                </h3>
                                            </v-col>
                                        </v-row>
                                        <v-row class="align-center justify-center">
                                            <v-col class="flex-grow-0">
                                                <v-hover v-slot:default="{ hover }">
                                                    <v-avatar
                                                        size="124"
                                                        color="primary lighten-3"
                                                        class="mx-12 mb-6"
                                                    >
                                                        <v-img
                                                            v-if="avatarLink"
                                                            :src="avatarLinkSmall"
                                                            @error="avatarError = true"
                                                        >
                                                        </v-img>
                                                        <v-icon v-else-if="!avatarLink || avatarError" x-large>
                                                            mdi-account-outline
                                                        </v-icon>
                                                        <v-expand-transition>
                                                            <v-card
                                                                v-if="hover"
                                                                rounded
                                                                ripple
                                                                class="d-flex transition-fast-in-fast-out primary darken-2 v-card--reveal caption white--text"
                                                                style="height: 100%;"
                                                                @click="
                                                                    openUploadForm
                                                                "
                                                            >
                                                                <v-icon
                                                                    color="white"
                                                                    x-large
                                                                >
                                                                    mdi-image-plus
                                                                </v-icon>
                                                            </v-card>
                                                        </v-expand-transition>
                                                    </v-avatar>
                                                </v-hover>
                                            </v-col>
                                        </v-row>
                                        <v-form
                                            @keyup.native.enter="
                                                if (profileFormChanged) {
                                                    save().profile();
                                                }
                                            "
                                        >
                                            <v-alert dense color="primary" type="info" v-if="identityDisclaimer">
                                                Ensure your name is exactly the same as it appears on your
                                                government issued ID (Passport or Drivers License) or Bank
                                                Statement
                                            </v-alert>
                                            <v-row>
                                                <v-col
                                                    cols="12"
                                                    sm="4"
                                                    class="py-1"
                                                >
                                                    <!-- First Name -->
                                                    <v-text-field
                                                        autocomplete="section-profile"
                                                        v-model="user.firstname"
                                                        v-validate="'name'"
                                                        type="text"
                                                        label="First Name"
                                                        name="first name"
                                                        color="primary"
                                                        outlined
                                                        :error-messages="
                                                            errors.first(
                                                                'first name'
                                                            )
                                                        "
                                                        @focus="
                                                            $event.target.select();
                                                            identityDisclaimer = true;
                                                        "
                                                        @blur="identityDisclaimer = false"
                                                        @change="
                                                            profileFormChanged = true
                                                        "
                                                        :disabled="nameIsVerified"
                                                    />
                                                </v-col>
                                                <v-col
                                                    cols="12"
                                                    sm="4"
                                                    class="py-1"
                                                >
                                                    <!-- Middle Name -->
                                                    <v-text-field
                                                        autocomplete="section-profile"
                                                        v-model="user.middlename"
                                                        v-validate="'name'"
                                                        type="text"
                                                        label="Middle Name"
                                                        name="middle name"
                                                        color="primary"
                                                        outlined
                                                        :error-messages="
                                                            errors.first(
                                                                'middle name'
                                                            )
                                                        "
                                                        @focus="
                                                            $event.target.select();
                                                            identityDisclaimer = true;
                                                        "
                                                        @blur="identityDisclaimer = false"
                                                        @change="
                                                            profileFormChanged = true
                                                        "
                                                        :disabled="nameIsVerified"
                                                    />
                                                </v-col>
                                                <v-col
                                                    cols="12"
                                                    sm="4"
                                                    class="py-1"
                                                >
                                                    <!-- Last Name -->
                                                    <v-text-field
                                                        autocomplete="section-profile"
                                                        v-model="user.lastname"
                                                        v-validate="'name'"
                                                        type="text"
                                                        label="Last Name"
                                                        name="last name"
                                                        color="primary"
                                                        outlined
                                                        :error-messages="
                                                            errors.first(
                                                                'last name'
                                                            )
                                                        "
                                                        @focus="
                                                            $event.target.select();
                                                            identityDisclaimer = true;
                                                        "
                                                        @blur="identityDisclaimer = false"
                                                        @change="
                                                            profileFormChanged = true
                                                        "
                                                        :disabled="nameIsVerified"
                                                    />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col class="py-1">
                                                    <dob-select
                                                        v-if="user.dob"
                                                        v-model="user.dob"
                                                        v-validate="'required|minimum-age'"
                                                        name="date of birth"
                                                        :error-messages="errors.first('date of birth')"
                                                        :disabled="dobIsVerified"
                                                        validate-on-blur
                                                    />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col class="py-1">
                                                    <!-- Email -->
                                                    <v-text-field
                                                        autocomplete="section-profile"
                                                        :value="user.email"
                                                        v-validate="'email'"
                                                        type="text"
                                                        label="Email"
                                                        name="email"
                                                        color="primary"
                                                        outlined
                                                        disabled
                                                    />
                                                </v-col>
                                            </v-row>
                                            <v-row v-if="identityVerificationData">
                                                <v-col v-if="identityVerificationData.isVerified">
                                                    <v-btn
                                                        class="px-0 pb-3"
                                                        color="primary"
                                                        large
                                                        text
                                                        @click="showIdentityVerificationProofs = true"
                                                    >
                                                        <v-icon x-large>
                                                            mdi-check
                                                        </v-icon>
                                                        Identity Verified at
                                                        <span>{{ identityVerificationData.verifiedAt }}</span>
                                                    </v-btn>
                                                </v-col>
                                                <v-dialog v-model="showIdentityVerificationProofs">
                                                    <v-card>
                                                        <v-card-title
                                                            class="headline"
                                                            primary-title
                                                            justify="center"
                                                        >
                                                            Identity Verification Proofs
                                                        </v-card-title>

                                                        <v-container>
                                                            <v-row cols="12">
                                                                <v-col
                                                                    v-for="(item, key) in identityVerificationData.proofs"
                                                                    :key="key"
                                                                    md="4"
                                                                    sm="4"
                                                                    xs="12"
                                                                    class="text-center"
                                                                >
                                                                    <v-img :src="item.proof" />
                                                                    <v-btn
                                                                        :href="item.proof"
                                                                        color="primary"
                                                                        text
                                                                        target="_blank"
                                                                        class="my-3"
                                                                    >
                                                                        <v-icon>mdi-link</v-icon>
                                                                        View
                                                                    </v-btn>
                                                                </v-col>
                                                            </v-row>
                                                        </v-container>

                                                        <v-divider />

                                                        <v-card-actions>
                                                            <v-spacer />
                                                            <v-btn
                                                                text
                                                                color="primary"
                                                                @click="showIdentityVerificationProofs = false"
                                                            >
                                                                close
                                                            </v-btn>
                                                        </v-card-actions>
                                                    </v-card>
                                                </v-dialog>
                                            </v-row>
                                        </v-form>
                                    </v-col>
                                </v-row>
                                <v-card-actions class="mx-3 mb-1 pt-0">
                                    <v-spacer />
                                    <v-btn
                                        :disabled="!profileFormChanged"
                                        large
                                        color="primary"
                                        text
                                        @click="save().profile()"
                                    >
                                        Save
                                    </v-btn>
                                </v-card-actions>
                            </v-card>

                            <v-expansion-panel
                                ref="address-panel"
                                style="order: 2"
                            >
                                <v-expansion-panel-header>
                                    Contact Information
                                </v-expansion-panel-header>
                                <v-expansion-panel-content eager>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon medium class="verify"
                                                    @click="beginAddressVerification"
                                                    v-bind="attrs"
                                                    v-on="on"
                                            >mdi-home-map-marker</v-icon>
                                        </template>
                                        <span>Verify Address</span>
                                    </v-tooltip>

                                    <v-form
                                        id="contact-information"
                                        @keyup.native.enter="save().address()"
                                        class="mt-5"
                                        data-vv-scope="contact-information"
                                    >
                                        <div class="pt-6">
                                            <!-- Street Number -->
                                            <v-text-field
                                                autocomplete="section-address address-street-number"
                                                v-validate="'required'"
                                                v-model="user.street_number"
                                                type="text"
                                                label="Street Number"
                                                name="street_number"
                                                color="primary"
                                                outlined
                                                :error-messages="
                                                    errors.first('contact-information.street_number')
                                                "
                                                @focus="$event.target.select()"
                                            />

                                            <!-- Street Name -->
                                            <v-text-field
                                                autocomplete="section-address address-level1"
                                                v-model="user.address"
                                                aria-required
                                                v-validate="'required'"
                                                required
                                                type="text"
                                                label="Street"
                                                name="street"
                                                color="primary"
                                                outlined
                                                :error-messages="
                                                    errors.first('contact-information.street')
                                                "
                                                @focus="$event.target.select()"
                                            />
                                            <!-- City -->
                                            <v-text-field
                                                autocomplete="section-address shipping address-level2"
                                                v-model="user.city"
                                                v-validate="'required'"
                                                aria-required
                                                required
                                                type="text"
                                                label="City"
                                                name="city"
                                                color="primary"
                                                outlined
                                                :error-messages="
                                                    errors.first('contact-information.city')
                                                "
                                                @focus="$event.target.select()"
                                            />

                                            <!-- State -->
                                            <v-text-field
                                                v-if="userCountry.abbreviation!=='US'"
                                                autocomplete="section-address region"
                                                v-model="user.state"
                                                v-validate="'required'"
                                                aria-required
                                                required
                                                type="text"
                                                label="State/Province/Region"
                                                name="state"
                                                color="primary"
                                                outlined
                                                :error-messages="
                                                    errors.first('contact-information.state')
                                                "
                                                @focus="$event.target.select()"
                                            />

                                            <v-autocomplete
                                                v-else
                                                v-model="user.state"
                                                v-validate="'required'"
                                                aria-required
                                                required
                                                :error-messages="
                                                    errors.first('contact-information.state')
                                                "
                                                name="state"
                                                :items="countryStates"
                                                label="State/Province/Region"
                                                item-text="name"
                                                item-value="name"
                                                item-color="primary"
                                                color="primary"
                                                @focus="$event.target.select()"
                                                outlined
                                            >
                                            </v-autocomplete>

                                            <!-- Country -->
                                            <v-text-field
                                                ref="country"
                                                v-model="user.country"
                                                v-validate="'required'"
                                                aria-required
                                                required
                                                type="text"
                                                name="country"
                                                label="Country"
                                                color="primary"
                                                outlined
                                                :disabled="true"
                                            />

                                            <!-- Zipcode -->
                                            <v-text-field
                                                autocomplete="section-address postal-code"
                                                v-model="user.zipcode"
                                                v-validate="'required'"
                                                type="text"
                                                label="Zip Code/Postal Code"
                                                name="zip code"
                                                color="primary"
                                                outlined
                                                :error-messages="
                                                    errors.first('contact-information.zip code')
                                                "
                                                @focus="$event.target.select()"
                                            />

                                            <!-- Phone -->
                                            <v-phone-input
                                                v-model="user.phone"
                                                v-validate="'phone-plus|phone-length'"
                                                :country="$_user.country_abbreviation"
                                                :enable-searching-country="true"
                                                :error-messages="errors.first('contact-information.phone')"
                                                display-format="international"
                                                hint="Enter phone number starting with country code e.g. +1, +234, +44, etc."
                                                label="Phone"
                                                name="phone"
                                                color="primary"
                                                outlined
                                            />
                                        </div>

                                        <v-row>
                                            <!-- <v-col v-if="identityVerificationData.isVerified">
                                                <v-btn large color="primary" text @click="showIdentityVerificationProofs = true">
                                                    <v-icon large x-large>
                                                        mdi-check
                                                    </v-icon>
                                                    Identity Verified at
                                                    <span>{{ identityVerificationData.verifiedAt }}</span>
                                                </v-btn>
                                            </v-col>
                                            <v-dialog v-model="showIdentityVerificationProofs">
                                                <v-card>
                                                    <v-card-title
                                                        class="headline"
                                                        primary-title
                                                        justify="center"
                                                    >
                                                        Identity Verification Proofs
                                                    </v-card-title>

                                                    <v-container>
                                                        <v-row cols="12">
                                                        <v-col md="4" sm="4" xs="12" class="text-center" v-for="(item, key) in identityVerificationData.proofs" :key="key">
                                                            <v-img :src="item.proof"/>
                                                            <v-btn :href="item.proof" color="primary" text target="_blank" class="my-3">
                                                            <v-icon>mdi-link</v-icon>
                                                            View
                                                            </v-btn>
                                                        </v-col>
                                                        </v-row>
                                                    </v-container>

                                                    <v-divider></v-divider>

                                                    <v-card-actions>
                                                        <v-spacer></v-spacer>
                                                        <v-btn
                                                            text
                                                            color="primary"
                                                            @click="showIdentityVerificationProofs = false"
                                                        >
                                                            close
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-card>
                                            </v-dialog> -->
                                        </v-row>

                                        <v-dialog
                                            v-model="isTfaDialogShown"
                                            max-width="450"
                                        >
                                            <confirm-phone
                                                ref="confirmPhoneNumberComponent"
                                                :phoneNumber="user.phone"
                                                style="background-color: #fff;"
                                                @success="onTfaSuccess"
                                                @error="onTfaError"
                                                :show.sync="isTfaDialogShown"
                                            />
                                        </v-dialog>

                                        <v-card-actions class="pa-0">
                                            <v-spacer />
                                            <v-btn
                                                large
                                                color="primary"
                                                text
                                                @click="save().address()"
                                            >
                                                Save
                                            </v-btn>
                                        </v-card-actions>
                                        <v-row class="align-content-center">
                                            <v-col
                                                class="d-flex align-center justify-center"
                                            >
                                                <v-sheet
                                                    color="grey"
                                                    width="100%"
                                                    height="1px"
                                                />
                                            </v-col>
                                            <v-col
                                                class="flex-grow-0 flex-shrink-1"
                                            >
                                                <h2
                                                    class="overline text-no-wrap"
                                                >
                                                    Other addresses
                                                </h2>
                                            </v-col>
                                            <v-col
                                                class="d-flex align-center justify-center"
                                            >
                                                <v-sheet
                                                    color="grey"
                                                    width="100%"
                                                    height="1px"
                                                />
                                            </v-col>
                                        </v-row>
                                        <user-secondary-addresses @loading="loading = true" @loaded="loading = false" />
                                    </v-form>
                                </v-expansion-panel-content>
                            </v-expansion-panel>

                            <v-expansion-panel
                                v-if="!userCountry.skip_payment_method_check"
                                ref="delivery-method-panel"
                                :style="{
                                    order: $_userCanPostOffers ? 3 : 4,
                                }"
                            >
                                <v-expansion-panel-header @click.once="showMethodsDisclaimer('delivery')">
                                    <div>
                                        Delivery Methods <br />
                                        <span class="text-center" style="color: #181235;" v-if="userStore.can_post_offers">
                                            (Register bank account or mobile money to receive money)
                                        </span>
                                        <span style="color: #181235;" v-else>
                                            (Register bank here to receive money in your bank account in {{ $store.state.user.country }})
                                        </span>
                                    </div>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <div class="pt-6">
                                        <v-row class="align-content-center">
                                            <v-col class="d-flex align-center justify-center">
                                                <v-sheet
                                                    color="grey"
                                                    width="100%"
                                                    height="1px"
                                                />
                                            </v-col>
                                            <v-col class="flex-grow-0 flex-shrink-1">
                                                <h2 class="overline text-no-wrap">
                                                    <v-btn
                                                        x-small
                                                        dark
                                                        class="ml-1"
                                                        color="primary"
                                                        icon
                                                        @click="showTutorialVideo('delivery')"
                                                    >
                                                        <v-icon>mdi-information</v-icon>
                                                    </v-btn>
                                                    Link Your Bank Account
                                                </h2>
                                            </v-col>
                                            <v-col class="d-flex align-center justify-center">
                                                <v-sheet
                                                    color="grey"
                                                    width="100%"
                                                    height="1px"
                                                />
                                            </v-col>
                                        </v-row>

                                        <delivery-method
                                            :mode="deliveryMethodNeedsUpdate ? 'update' : ''"
                                            @loading="loading = true"
                                            @loaded="loading = false"
                                            @saved="fetchUser"
                                            @scroll-to-address="scrollToAddress"
                                        />
                                    </div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>

                            <v-expansion-panel
                                v-if="!userCountry.skip_payment_method_check"
                                ref="payment-method-panel"
                                :style="{
                                    order: $_userCanPostOffers ? 4 : 3,
                                }"
                            >
                                <v-expansion-panel-header @click.once="showMethodsDisclaimer('payment')">
                                    <div>
                                        Payment Methods <br />
                                        <span style="color: #181235;">
                                            ({{
                                                userStore.can_post_offers ? "Register debit card here to send money" : "Register bank here to send money"
                                            }})
                                        </span>
                                    </div>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <div class="pt-6">
                                        <v-row class="align-content-center">
                                            <template v-if="userCountry.abbreviation === 'NG' && userStore.can_post_offers">
                                                <v-col cols="12">
                                                    <do-not-have-debit-card-checkbox
                                                        @loading="loading = true"
                                                        @loaded="loading = false"
                                                    />
                                                </v-col>
                                                <v-col cols="12">
                                                    <v-sheet
                                                        color="grey"
                                                        width="100%"
                                                        height="1px"
                                                    />
                                                </v-col>
                                            </template>
                                            <v-col cols="12">
                                                <h2 class="overline text-no-wrap text-center">
                                                    {{ userStore.can_post_offers ? 'Add your debit card' : 'Link your bank account' }}
                                                </h2>
                                            </v-col>
                                            <v-col cols="12" v-if="!userStore.can_post_offers">
                                                <v-sheet
                                                    color="grey"
                                                    width="100%"
                                                    height="1px"
                                                />
                                            </v-col>
                                        </v-row>

                                        <payment-methods
                                            @loading="loading = true"
                                            @loaded="loading = false"
                                            @saved="onPaymentMethodSaved"
                                            @verify="beginIdentityVerification"
                                            @scroll-to-address="scrollToAddress"
                                        ></payment-methods>

                                        <div class="text-center mt-10">
                                            <v-btn
                                                dark
                                                text
                                                class="text-none font-weight-bold text-decoration-underline"
                                                color="#858080"
                                                @click="showTutorialVideo('payment')"
                                            >
                                                How to link {{ userStore.can_post_offers ? 'debit card' : 'bank account'}}?
                                            </v-btn>
                                        </div>
                                    </div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>

                            <v-expansion-panel style="order: 5">
                                <v-expansion-panel-header>
                                    Customer Support Officer
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-card-text>
                                        <v-row class="align-content-center">
                                            <v-col
                                                cols="12"
                                                md="3"
                                            >
                                                <v-icon>
                                                    mdi-account-outline
                                                </v-icon>
                                            </v-col>
                                            <v-col
                                                :class="{
                                                    'text-right': $vuetify.breakpoint.mdAndUp,
                                                    'pt-0': $vuetify.breakpoint.mobile,
                                                }"
                                            >
                                                <p class="body-1 subtitle-1 flex-grow-0 mb-0 font-weight-bold">
                                                    {{ accountOfficer.firstname }}
                                                </p>
                                            </v-col>
                                        </v-row>
                                        <v-row class="align-content-center">
                                            <v-col
                                                cols="12"
                                                md="3"
                                            >
                                                <v-icon>
                                                    mdi-email-outline
                                                </v-icon>
                                            </v-col>
                                            <v-col
                                                :class="{
                                                    'text-right': $vuetify.breakpoint.mdAndUp,
                                                    'pt-0': $vuetify.breakpoint.mobile,
                                                }"
                                            >
                                                <p class="body-1 subtitle-1 flex-grow-0 mb-0 font-weight-bold">
                                                    <a :href="`mailto:${accountOfficer.email}`">
                                                        {{ accountOfficer.email }}

                                                        <v-btn icon>
                                                            <v-icon color="grey">
                                                                mdi-open-in-new
                                                            </v-icon>
                                                        </v-btn>
                                                    </a>
                                                </p>
                                            </v-col>
                                        </v-row>
                                        <v-row class="align-content-center">
                                            <v-col
                                                cols="12"
                                                md="3"
                                            >
                                                <v-icon>
                                                    mdi-whatsapp
                                                </v-icon>
                                            </v-col>
                                            <v-col
                                                :class="{
                                                    'text-right': $vuetify.breakpoint.mdAndUp,
                                                    'pt-0': $vuetify.breakpoint.mobile,
                                                }"
                                            >
                                                <p class="body-1 subtitle-1 flex-grow-0 mb-0 font-weight-bold">
                                                    <a :href="`https://wa.me/${accountOfficer.phone && accountOfficer.phone.substring(1)}`">
                                                        {{ accountOfficer.phone }}

                                                        <v-btn icon>
                                                            <v-icon color="grey">
                                                                mdi-open-in-new
                                                            </v-icon>
                                                        </v-btn>
                                                    </a>
                                                </p>
                                            </v-col>
                                        </v-row>
                                        <v-row class="align-content-center">
                                            <v-col
                                                cols="12"
                                                md="3"
                                            >
                                                <v-icon>
                                                    mdi-twitter
                                                </v-icon>
                                            </v-col>
                                            <v-col
                                                :class="{
                                                    'text-right': $vuetify.breakpoint.mdAndUp,
                                                    'pt-0': $vuetify.breakpoint.mobile,
                                                }"
                                            >
                                                <p class="body-1 subtitle-1 flex-grow-0 mb-0 font-weight-bold">
                                                    <a href="https://twitter.com/messages/compose?text=Hello&recipient_id=1177321542574845952">
                                                        @kaoshinetwork

                                                        <v-btn icon>
                                                            <v-icon color="grey">
                                                                mdi-open-in-new
                                                            </v-icon>
                                                        </v-btn>
                                                    </a>
                                                </p>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-expansion-panel-content>
                            </v-expansion-panel>

                            <v-expansion-panel style="order: 6">
                                <v-expansion-panel-header>
                                    Password
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-form @keyup.native.enter="save().password()">
                                        <v-row class="align-content-center pt-6">
                                            <v-col
                                                class="d-flex align-center justify-center"
                                            >
                                                <v-sheet
                                                    color="grey"
                                                    width="100%"
                                                    height="1px"
                                                />
                                            </v-col>
                                            <v-col
                                                class="flex-grow-0 flex-shrink-1"
                                            >
                                                <h2 class="overline text-no-wrap">
                                                    Change Password
                                                </h2>
                                            </v-col>
                                            <v-col
                                                class="d-flex align-center justify-center"
                                            >
                                                <v-sheet
                                                    color="grey"
                                                    width="100%"
                                                    height="1px"
                                                />
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col class="pb-0">
                                                <!-- Current Password -->
                                                <v-text-field
                                                    autocomplete="section-security current-password"
                                                    v-model="user.old_password"
                                                    name="password"
                                                    label="Password"
                                                    outlined
                                                    color="primary"
                                                    :append-icon="
                                                        passwordVisible
                                                            ? 'mdi-eye-off'
                                                            : 'mdi-eye'
                                                    "
                                                    @click:append="
                                                        passwordVisible = !passwordVisible
                                                    "
                                                    color:append-icon="primary"
                                                    :type="
                                                        passwordVisible
                                                            ? 'text'
                                                            : 'password'
                                                    "
                                                ></v-text-field>

                                                <!-- New Password -->
                                                <v-text-field
                                                    autocomplete="section-security new-password"
                                                    v-model="user.password"
                                                    :rules="[rules.passwordIsValid]"
                                                    validate-on-blur
                                                    hint="At least 6 characters"
                                                    label="New Password"
                                                    name="new password"
                                                    color="primary"
                                                    outlined
                                                    :type="
                                                        passwordVisible
                                                            ? 'text'
                                                            : 'password'
                                                    "
                                                />

                                                <!-- New Password Confirm -->
                                                <v-text-field
                                                    autocomplete="off"
                                                    v-model="confirm_password"
                                                    :rules="[rules.passwordsMatch]"
                                                    validate-on-blur
                                                    ref="password-confirmation"
                                                    name="confirm new password"
                                                    label="Confirm New Password"
                                                    outlined
                                                    color="primary"
                                                    :type="
                                                        passwordVisible
                                                            ? 'text'
                                                            : 'password'
                                                    "
                                                />
                                            </v-col>
                                        </v-row>

                                        <v-card-actions class="pa-0">
                                            <v-spacer />
                                            <v-btn
                                                large
                                                color="primary"
                                                text
                                                @click="save().password()"
                                            >
                                                Save
                                            </v-btn>
                                        </v-card-actions>
                                    </v-form>
                                </v-expansion-panel-content>
                            </v-expansion-panel>

                            <v-expansion-panel
                                v-if="enableFeatureTwoFactorAuth"
                                style="order: 7"
                            >
                                <v-expansion-panel-header>
                                    Two-Factor Authentication
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-form @keyup.native.enter="save().twoFactorAuth()">
                                        <div>
                                            <v-row class="align-content-center">
                                                <v-col
                                                    class="d-flex align-center justify-center"
                                                >
                                                    <v-sheet
                                                        color="grey"
                                                        width="100%"
                                                        height="1px"
                                                    />
                                                </v-col>
                                                <v-col
                                                    class="flex-grow-0 flex-shrink-1"
                                                >
                                                    <h2
                                                        class="overline text-no-wrap"
                                                    >
                                                        Two-Factor Authentication
                                                    </h2>
                                                </v-col>
                                                <v-col
                                                    class="d-flex align-center justify-center"
                                                >
                                                    <v-sheet
                                                        color="grey"
                                                        width="100%"
                                                        height="1px"
                                                    />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col>
                                                    <!-- Enable Two-Factor Authentication -->
                                                    <v-switch
                                                        label="Enable Multi-Factor Authentication?"
                                                        name="tfa_required_for_login"
                                                        v-model="user.tfa_required_for_login"
                                                        color="primary"
                                                        class="ultraDense"
                                                        hide-details
                                                    />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <v-card-actions class="pa-0">
                                            <v-spacer />
                                            <v-btn
                                                large
                                                color="primary"
                                                text
                                                @click="save().twoFactorAuth()"
                                            >
                                                Save
                                            </v-btn>
                                        </v-card-actions>
                                    </v-form>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                            <v-expansion-panel style="order: 8">
                                <v-expansion-panel-header>
                                    Privacy
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-row class="pt-6">
                                        <v-col>
                                            <!-- Show My Name -->
                                            <v-switch
                                                v-model="userSettings.show_name"
                                                class="ultraDense"
                                                label="Show my name?"
                                                name="show name"
                                                hide-details
                                                color="primary"
                                            />
                                        </v-col>
                                        <v-col>
                                            <!-- Show My Picture -->
                                            <v-switch
                                                v-model="userSettings.show_avatar"
                                                class="ultraDense"
                                                label="Show my picture?"
                                                name="show avatar"
                                                hide-details
                                                color="primary"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-card-actions class="pa-0">
                                        <v-spacer />
                                        <v-btn
                                            large
                                            color="primary"
                                            text
                                            @click="save().privacy()"
                                        >
                                            Save
                                        </v-btn>
                                    </v-card-actions>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                            <v-expansion-panel style="order: 9">
                                <v-expansion-panel-header>
                                    Notifications
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <div class="pt-6 pb-5">
                                        <v-row class="align-content-center">
                                            <v-col
                                                class="d-flex align-center justify-center"
                                            >
                                                <v-sheet
                                                    color="grey"
                                                    width="100%"
                                                    height="1px"
                                                />
                                            </v-col>
                                            <v-col
                                                class="flex-grow-0 flex-shrink-1"
                                            >
                                                <h2 class="overline text-no-wrap">
                                                    Receive Notifications Via
                                                </h2>
                                            </v-col>
                                            <v-col
                                                class="d-flex align-center justify-center"
                                            >
                                                <v-sheet
                                                    color="grey"
                                                    width="100%"
                                                    height="1px"
                                                />
                                            </v-col>
                                        </v-row>

                                        <v-row>
                                            <v-col>
                                                <!-- SMS -->
                                                <v-switch
                                                    v-model="
                                                        userSettings.notify_sms
                                                    "
                                                    label="SMS"
                                                    name="sms"
                                                    hide-details
                                                    color="primary"
                                                    class="ultraDense"
                                                    :readonly="!userSettings.notify_email && userSettings.notify_sms"
                                                    @change="save().notifications()"
                                                />
                                            </v-col>
                                            <v-col>
                                                <!-- Email -->
                                                <v-switch
                                                    v-model="
                                                        userSettings.notify_email
                                                    "
                                                    label="Email"
                                                    name="email"
                                                    hide-details
                                                    color="primary"
                                                    class="ultraDense"
                                                    :readonly="!userSettings.notify_sms && userSettings.notify_email"
                                                    @change="save().notifications()"
                                                />
                                            </v-col>
                                        </v-row>
                                    </div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>

                            <v-expansion-panel
                                v-if="!userStore.can_post_offers"
                                style="order: 10"
                            >
                                <v-expansion-panel-header>Download Plugin/Extension</v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <div class="extension text-center">
                                        <v-btn
                                            icon
                                            target="_blank"
                                            class="my-10"
                                            href="https://chrome.google.com/webstore/detail/kaoshi-sticky-note/kjoampgakgfjlflmfhmaielefckchfcd">
                                            <v-icon
                                                color="primary"
                                                x-large
                                            >mdi-download</v-icon>
                                        </v-btn>
                                        <p style="font-size: 16px">
                                            Kaoshi Sticky Notes, is a one-click extension designed to save you time and energy when shopping across multiple remittance websites.
                                            With just a click, Kaoshi Sticky Notes instantly autofills forms requiring your information and your recipient's information on these remittance websites. Try it now, click the download icon
                                        </p>
                                    </div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>

                            <v-expansion-panel
                                v-if="isDevOrLocal"
                                style="order: 11"
                            >
                                <v-expansion-panel-header>Enable Kaoshi Test Pay</v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <div class="extension text-center">
                                        <v-col cols="12">
                                            <enable-kaoshi-test-payment
                                                @loading="loading = true"
                                                @loaded="loading = false"
                                            />
                                        </v-col>
                                    </div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </template>
                </v-col>
            </v-row>
            <form class="upload-form" @submit.prevent="">
                <input
                    id="userpic"
                    placeholder="Browse computer"
                    type="file"
                    @change="uploadUserAvatar($event)"
                />
            </form>
        </v-col>

        <DocVerificationForm
            v-model="showIdentityVerificationForm"
            type="identity"
        />

        <DocVerificationForm
            v-model="showAddressVerificationForm"
            type="address"
        />

        <ContactInfoUpdatedModal
            v-model="showContactInfoUpdatedModal"
        />
    </v-container>
</template>

<script>
    import { mapGetters, mapState } from 'vuex';

    import ServerUtils from '../services/ServerUtils';
    import DeliveryMethod from './components/delivery-method.vue';
    import PaymentMethods from './components/payment-methods.vue';
    import UserSecondaryAddresses from './components/user-secondary-addresses.vue';
    import dobSelect from './components/dob-select.vue';
    import confirmPhone from './components/confirmPhone.vue';
    import DocVerificationForm from './components/doc-verification-form.vue';
    import countriesArray from '../modules/countries.json';
    import DoNotHaveDebitCardCheckbox from './components/doNotHaveDebitCardCheckbox.vue';
    import EnableKaoshiTestPayment from './components/settings/enable-kaoshi-test-payment.vue';
    import ContactInfoUpdatedModal from './components/settings/contact-info-updated-modal.vue';

    export default {
        watch: {
            '$route.hash': {
                immediate: true,
                handler() {
                    if (this.$route.hash) {
                        const hash = this.$route.hash.substr(1);
                        if (hash === 'address') {
                            this.panel = 0;
                            this.scrollToRef('address-panel');
                        }
                        if (hash === 'delivery-method') {
                            this.panel = 1;
                            this.scrollToRef('delivery-method-panel');
                        }
                        if (hash === 'payment-methods') {
                            this.panel = 2;
                            this.scrollToRef('payment-method-panel');
                        }
                    }
                },
            },
        },
        mounted,
        components: {
            'delivery-method': DeliveryMethod,
            'payment-methods': PaymentMethods,
            'user-secondary-addresses': UserSecondaryAddresses,
            'dob-select': dobSelect,
            confirmPhone,
            DocVerificationForm,
            DoNotHaveDebitCardCheckbox,
            EnableKaoshiTestPayment,
            ContactInfoUpdatedModal,
        },
        methods: getMethods(),
        computed: getComputed(),
        data,
    };

async function mounted() {
    const vm = this;

    this.loadCountries();

    function getUserSettings() {
        function success(response) {
            vm.loading = false;
            vm.userSettings = response.data;
            vm.$emit('user-settings-loaded');

            if (ServerUtils.isDevOrLocal() && response.data.hasOwnProperty('enable_kaoshi_test_payment')) {
                vm.$store.commit('storeTestPaymentStatus', response.data['enable_kaoshi_test_payment']);
            }
        }

        function error(err) {
            vm.loading = false;
            vm.$swal({
                type: 'error',
                title: 'Error loading user settings',
                text: err
            });
        }

        vm.loading = true;
        return Vue.backendApi()
            .user(vm.$store.state.user.id)
            .getUserSettings()
            .then(success, error);
    }

    await this.fetchUser();
    await getUserSettings();

    this.getIdentityVerificationData();
}

function getMethods() {
    return {
        loadCountries,
        fetchUser,
        updateUserFromStore,
        save,
        openUploadForm,
        uploadUserAvatar,
        getIdentityVerificationData,
        onPaymentMethodSaved,
        beginAddressVerification,
        beginIdentityVerification,
        showMethodsDisclaimer,
        scrollToRef,
        confrimPhoneNumber,
        onTfaSuccess,
        onTfaError,
        scrollToAddress,
    };

        async function loadCountries() {
            try {
                const { data: countries } = await Vue.backendApi()
                    .countries()
                    .list();
                this.kaoshiCountries = countries;
            } catch (error) {
                this.$swal({
                    type: 'error',
                    title: 'Error loading countries',
                    text: error,
                });
            }
        }

        /**
         * Open Address settings tab and then
         * back user to delivery/payment method tab
         */
        function scrollToAddress(redirectToHash) {
            this.$router.push({
                name: 'user-settings',
                params: {
                    redirectTo: {
                        name: 'user-settings',
                        hash: `#${redirectToHash}`,
                    },
                },
                hash: '#address',
            });
        }

    function fetchUser() {
        this.loading = true;
        return Vue.backendApi()
            .user(this.$store.state.user.id)
            .getUser()
            .then((response) => {
                this.loading = false;
                this.$store.commit('storeUser', {
                    ...this.$store.state.user,
                    ...response.data
                });
                this.updateUserFromStore.apply();
            })
            .catch((err) => {
                this.loading = false;
                this.$swal({
                    type: 'error',
                    title: 'Error loading user',
                    text: err
                });
            })
    }

    function scrollToRef(ref, delay = 1000, rate = 100) {
        setTimeout(() => {
          const interval = setInterval(() => {
              const element = this.$refs[ref].$el;
              if (element) {
                   element.scrollIntoView();
                   clearInterval(interval);
              }
          }, rate);
        }, delay);
    }

        function beginIdentityVerification() {
            this.$swal({
                type: 'info',
                title: 'Are you sure?',
                text: 'Ensure your name is exactly the same as it appears on your government issued ID (Passport or Drivers License) or Bank Statement',
                showCancelButton: true,
                confirmButtonText: 'Yes, I am sure!',
            }).then((result) => {
                if (result.value) {
                    this.showIdentityVerificationForm = true;
                    // this.$router.push('/user/identity-verification');
                }
            });
        }

        function beginAddressVerification() {
            this.$swal({
                type: 'info',
                title: 'Are you sure?',
                text: 'Ensure your name is the same as the document you provide for verifying your address - Bank Statement, Utility Bill, etc.',
                showCancelButton: true,
                confirmButtonText: 'Yes, I am sure!',
            }).then((result) => {
                if (result.value) {
                    this.showAddressVerificationForm = true;
                    // this.$router.push('/user/address-verification');
                }
            });
        }

    function showMethodsDisclaimer(type) {
        let text;

        if (this.userStore.can_post_offers) {
            if(type === 'payment') {
                text = 'Ensure the name on Kaoshi is exactly the same as it appears on your debit card';
            }
            if(type === 'delivery') {
                text = 'Ensure the name on Kaoshi is exactly the same as it appears on your bank statement or mobile money account';
            }
        } else {
            text = 'Ensure that the name on your bank statement matches the name on Kaoshi';
        }

        this.$swal({
            type: 'info',
            title: 'Information!',
            text,
        });
    }

    function updateUserFromStore() {
        const vm = this;

        vm.user.firstname = vm.userStore.firstname;
        vm.user.middlename = vm.userStore.middlename;
        vm.user.lastname = vm.userStore.lastname;
        vm.user.dob = vm.userStore.dob;
        vm.user.email = vm.userStore.email;
        vm.user.phone = vm.userStore.phone;
        vm.user.address = vm.userStore.address;
        vm.user.street_number = vm.userStore.street_number;
        vm.user.city = vm.userStore.city;
        vm.user.state = vm.userStore.state;
        vm.user.country = vm.userStore.country;
        vm.user.zipcode = vm.userStore.zipcode;
        vm.user.tfa_required_for_login = vm.userStore.tfa_required_for_login;
    }

    function save() {
        const vm = this;
        let payload;

        return {
            profile,
            password,
            twoFactorAuth,
            address,
            privacy,
            notifications,
        };

        function storeUserSettings(payload) {
            vm.loading = true;
            Vue.backendApi()
                .user(vm.$store.state.user.id)
                .storeUserSettings(payload)
                .then(success, error);

            function success(response) {
                vm.loading = false;
                vm.userSettings = response.data;
            }

            function error(err) {
                vm.loading = false;
                if (err) {
                    vm.$swal({
                        type: 'error',
                        title: `Error saving your notification settings`,
                        text: err
                    });
                }
            }
        }

        function storeUser(payload, successMessage) {
            vm.loading = true;
            Vue.backendApi()
                .user(vm.$store.state.user.id)
                .storeUser(payload)
                .then(success, error);

            function success(response) {
                vm.loading = false;
                vm.$store.commit('storeUser', response.data);
                vm.updateUserFromStore();
                vm.profileFormChanged = false;
                vm.$swal({
                    target: document.getElementById('swal2-container'),
                    type: 'success',
                    title: successMessage,
                    onAfterClose: () => {
                        const { redirectTo } = vm.$route.params;
                        if(redirectTo) {
                            vm.$router.push(redirectTo);
                        }
                    },
                });
            }

            function error(err) {
                vm.loading = false;
                vm.$swal({
                    target: document.getElementById('swal2-container'),
                    type: 'error',
                    title: 'Error saving your profile information',
                    text: err
                });
            }
        }

        function profile() {
            payload = {
                firstname: vm.user.firstname,
                lastname: vm.user.lastname,
            };

            if (vm.user.middlename && vm.user.middlename.length > 0) {
                payload.middlename = vm.user.middlename;
            }

            if (vm.user.dob) {
                payload.dob = vm.user.dob;
            }

            storeUser(payload, 'Profile saved successfully!');
        }

        function password() {
            vm.loading = true;
            const formIsValid = vm.rules.passwordIsValid && vm.rules.passwordsMatch;

            if (formIsValid) {
                changePassword();
            }

            function changePassword() {
                vm.loading = true;

                return new Promise((resolve) => {
                    Vue.backendApi()
                        .user(vm.$store.state.user.id)
                        .changePassword(vm.user)
                        .then(success, error);

                    function success(response) {
                        vm.loading = false;
                        vm.user.old_password = '';
                        vm.user.password = '';
                        vm.confirm_password = '';
                        vm.$swal({
                            target: document.getElementById('swal2-container'),
                            type: 'success',
                            title: 'Password successfully changed!'
                        });
                    }

                    function error(err) {
                        vm.loading = false;
                        vm.$swal({
                            target: document.getElementById('swal2-container'),
                            type: 'error',
                            title: err
                        });
                    }
                });
            }
        }

        function twoFactorAuth() {
            const payload = {
              tfa_required_for_login: vm.user.tfa_required_for_login,
            };

            Vue.backendApi()
                .user(vm.$store.state.user.id)
                .storeTwoFactorAuth(payload)
                .then(success, error);

            function success(response) {
                let user = response.data;
                vm.$store.commit('storeUser', user);
                vm.updateUserFromStore();
                vm.$swal({
                    target: document.getElementById('swal2-container'),
                    type: 'success',
                    title: 'Two factor auth settings saved!'
                });
            }

            function error(err) {
                vm.loading = false;
                vm.$swal({
                    target: document.getElementById('swal2-container'),
                    type: 'error',
                    title: err
                });
            }
        }

        function address() {
            vm.$validator.validateAll('contact-information').then(async (result) => {
                if(result) {
                    payload = {
                        address: vm.user.address,
                        street_number: vm.user.street_number,
                        city: vm.user.city,
                        state: vm.user.state,
                        phone: vm.user.phone,
                        zipcode: vm.user.zipcode,
                    };

                    if (payload.phone !== vm.userStore.phone) {
                        await vm.confrimPhoneNumber();
                    }

                        if (vm.onboarding) {
                            if (vm.userStore.can_post_offers) {
                                vm.$router.push({
                                    name: 'user-settings',
                                    hash: '#delivery-method',
                                });
                            } else {
                                vm.showContactInfoUpdatedModal = true;
                            }
                        }

                    return storeUser(payload, 'Address saved successfully!');
                }
            });
        }

        function privacy() {
            vm.loading = true;

            payload = {
                show_avatar: vm.userSettings.show_avatar,
                show_name: vm.userSettings.show_name
            };

            Vue.backendApi()
                .user(vm.$store.state.user.id)
                .storePrivacySettings(payload)
                .then(success, error);

            function success(response) {
                vm.loading = false;
                vm.userSettings.show_avatar = response.data.show_avatar;
                vm.userSettings.show_name = response.data.show_name;
                vm.$store.commit('loadUserSettings', response.data);

                const text = vm.userSettings.show_avatar && vm.userSettings.show_name
                        ? `Your name and picture will be displayed`
                        : vm.userSettings.show_avatar ? `Only your picture will be displayed`
                        : vm.userSettings.show_name ? `Only your name will be displayed`
                        : `Your name and picture will not be displayed`;

                vm.$swal({
                    target: document.getElementById('swal2-container'),
                    type: 'success',
                    title: 'Privacy Settings updated!',
                    text,
                });
            }

            function error(error) {
                vm.loading = false;

                vm.$swal({
                    target: document.getElementById('swal2-container'),
                    type: 'error',
                    title: 'Error saving privacy settings',
                    text: error
                });
            }
        }

            function notifications() {
                payload = {
                    notify_sms: vm.userSettings.notify_sms,
                    notify_whatsapp: vm.userSettings.notify_whatsapp,
                    notify_telegram: vm.userSettings.notify_telegram,
                    notify_wechat: vm.userSettings.notify_wechat,
                    notify_email: vm.userSettings.notify_email,
                };

                vm.loading = true;
                Vue.backendApi()
                    .user(vm.$store.state.user.id)
                    .storeNotificationSettings(payload)
                    .then(success, error);

                function success(response) {
                    vm.loading = false;
                    Object.keys(vm.userSettings).forEach((key) => {
                        if (response.data.hasOwnProperty(key)) {
                            vm.userSettings[key] = response.data[key];
                        }
                    });

                    const { notify_sms, notify_email } = payload

                    const text = notify_sms && notify_email
                        ? `You will be notified via SMS and e-mail`
                        : notify_sms ? `You will only be notfied via SMS`
                        : notify_email ? `You will only be notfied via e-mail`
                        : `You will not be notified via SMS or e-mail`;

                    vm.$swal({
                        target: document.getElementById('swal2-container'),
                        type: 'success',
                        title: 'Notifications Settings updated!',
                        text,
                    });
                }

                function error(err) {
                    vm.loading = false;
                    if (err) {
                        vm.$swal({
                            type: 'error',
                            title: `Error saving your notification settings`,
                            text: err
                        });
                    }
                }
            }
    }

    function openUploadForm() {
        $('input#userpic').click();
    }

    function uploadUserAvatar(event) {
        const vm = this;
        const fileSelected = event.target.files.length > 0;
        if (!fileSelected) {
            return;
        }

        vm.loading = true;
        Vue.backendApi()
            .file()
            .storeFile(event.target.files[0])
            .then(storeAvatarLink, uploadError);

        function storeAvatarLink(response) {
            const payload = {
                avatar: response.data.url
            };

            return Vue.backendApi()
                .user(vm.$store.state.user.id)
                .storeUser(payload)
                .then(success, storeLinkError);

            function success(response) {
                vm.loading = false;
                vm.$store.commit('storeUser', response.data);
            }

            function storeLinkError(err) {
                vm.loading = false;

                vm.$swal({
                    target: document.getElementById('swal2-container'),
                    type: 'error',
                    title: 'We were unable to add this upload to your profile',
                    text: err
                });
            }
        }

        function uploadError(err) {
            vm.loading = false;

            vm.$swal({
                target: document.getElementById('swal2-container'),
                type: 'error',
                title: 'Error uploading file',
                text: err
            });
        }
    }

    function getIdentityVerificationData() {
        return Vue.backendApi()
            .user(this.$store.state.user.id)
            .getIdentityVerificationData({})
            .then((response) => {
                this.identityVerificationData = response.data;
            })
            .catch((err) => {
                if (err) {
                    this.$swal({
                        type: 'error',
                        title: 'Error loading identity verification data',
                        text: err,
                    });
                }
            });
    }

    function onPaymentMethodSaved() {
        const { redirectTo } = this.$route.params;
        if (redirectTo) {
            this.$router.push(redirectTo);
        }

        this.fetchUser();
    }

    async function confrimPhoneNumber() {
        this.isTfaDialogShown = true;
        await this.$nextTick();
        return new Promise((resolve, reject) => {
            this.tfaDialogResolve = resolve;
            this.$refs.confirmPhoneNumberComponent.sendCode();
        })
    }

    function onTfaSuccess(response) {
        this.isTfaDialogShown = false;
        return this.tfaDialogResolve(true);
    }

    function onTfaError(error) {
        this.$swal({
            type: 'error',
            title: 'Error',
            text: error,
        });
    }
}

function getComputed() {
    return {
        ...mapState([
            'onboarding',
        ]),
        ...mapGetters([
            'userCountry',
        ]),
        avatarLink,
        avatarLinkSmall,
        userStore,
        nameIsVerified,
        dobIsVerified,
        enableFeatureTwoFactorAuth,
        deliveryMethodNeedsUpdate,
        countryStates,
        isDevOrLocal,
        accountOfficer,
    };

    function avatarLink() {
        if (this.$store.state.user.avatar) {
            return this.$store.state.user.avatar;
        }
    }

    /**
     * @todo Add small avatar url to api.
     */
    function avatarLinkSmall() {
        let link;
        if (this.$store.state.user.avatar) {
            link = this.$store.state.user.avatar;
        }
        return link;
    }

    function userStore() {
        return this.$store.state.user;
    }

    function nameIsVerified() {
      return this.userStore.name_verified_at === null ? false : true;
    }

    function dobIsVerified() {
      return this.userStore.dob_verified_at === null ? false : true;
    }

    function enableFeatureTwoFactorAuth() {
        return !!process.env.MIX_ENABLE_FEATURE_TWO_FACTOR_AUTH;
    }

    function deliveryMethodNeedsUpdate() {
        if (this.$route.hash) {
          const hash = this.$route.hash.substr(1);
          const { query } = this.$route;

          return hash === 'delivery-method' && !query.oauth_state_id; // is from PLAID OAuth redirect
        }

        return false;
    }

    function countryStates() {
        const country = countriesArray.find(country => country.iso2 === this.userCountry.abbreviation);
        if(!country) return [];

        return country.states.map(key => key.name);
    }

    function isDevOrLocal() {
        return ServerUtils.isDevOrLocal();
    }

    function accountOfficer() {
        return this.$_user.accountOfficer || {};
    }
}

function data() {
    return {
        avatarError: false,
        kaoshiCountries: [],
        identityDisclaimer: false,
        user: {},
        userSettings: {
            show_avatar: false,
            show_name: false,
            notify_email: false,
            notify_sms: false,
            notify_whatsapp: false,
            notify_telegram: false,
            notify_wechat: false
        },
        passwordVisible: false,
        confirm_password: '',
        loading: false,
        profileFormChanged: false,
        identityVerificationData: null,
        showIdentityVerificationForm: false,
        showIdentityVerificationProofs: null,
        showAddressVerificationForm: false,
        showContactInfoUpdatedModal: false,
        rules: {
            passwordIsValid: (value) => {
                const passwordRule =
                    '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{6,}';
                const passwordTester = RegExp(passwordRule);
                return (
                    passwordTester.test(this.user.password) ||
                    'Password must contain one lowercase, one uppercase, one number, and one special character.'
                );
            },
            passwordsMatch: (value) => {
                return (
                    this.user.password === this.confirm_password ||
                    'Password confirmation must match.'
                );
            }
        },
        panel: null,
        isTfaDialogShown: false,
        tfaDialogResolve: null,
    };
}
</script>

<style scoped>
.form {
    max-width: 500px;
}
.v-sheet--expansion-panels--header {
    border-radius: 4px 4px 0 0 !important;
    width: 100%;
}
.v-sheet--expansion-panels--footer {
    border-radius: 0 0 4px 4px !important;
    width: 100%;
    display: flex;
}
.v-expansion-panel--active > .v-expansion-panel-header {
    min-height: unset;
}
.theme--light.v-expansion-panels .v-expansion-panel:not(:last-child):after {
    border-color: rgba(0, 0, 0, 0.12);
}

.v-input--selection-controls.ultraDense {
    margin-top: 0;
    padding-top: 0;
}
.theme--light.v-expansion-panels.v-expansion-panels--focusable
    >>> .v-expansion-panel-header--active:before,
.theme--light.v-expansion-panels.v-expansion-panels--focusable
    >>> .v-expansion-panel-header--active:hover:before,
.theme--light.v-expansion-panels.v-expansion-panels--focusable
    >>> .v-expansion-panel-header:focus:before {
    background-color: #181235;
    opacity: 0.4;
}
.v-expansion-panels >>> .v-expansion-panel-header--active {
    color: #000;
    opacity: 1;
    z-index: 10000;
}
.v-card--reveal {
    align-items: center;
    bottom: 0;
    justify-content: center;
    opacity: 0.9;
    position: absolute;
    width: 100%;
}
.upload-form {
    visibility: hidden;
    z-index: 0;
    position: fixed;
}
.disabled {
    opacity: 0.2;
}

.verify {
    position: absolute !important;
    right: 0.3em !important;
    color: #181235 !important;
    font-family: Roboto, sans-serif !important;
    cursor: pointer;
}

#contact-information >>> .v-input:last-child .theme--light.v-messages {
    color: #181235;
}
</style>
